import { ApplicationTypes, attachComponent } from 'element-domain'

import {
  customPriceFooterComponent,
  customSummaryPageComponent,
  finalizeButtonComponent,
  finalizePageComponent,
  memoEval,
  propertyPageComponent,
  saveTemplateThumbnailComponent,
  sidebarTabsComponent,
} from '@salsita-ai/platform-project-sdk/domain'
import { Dialog } from '@salsita-ai/platform-project-sdk/ui'

import SummaryPage from '../ui/components/SummaryPage'
import FinalizeCartDialog from '../ui/components/dialogs/FinalizeCartDialog'
import FinalizeQuoteDialog from '../ui/components/dialogs/FinalizeQuoteDialog'
import CustomSummaryFooter from '../ui/components/helper-components/CustomSummaryFooter'
import { PriceFooter } from '../ui/components/helper-components/PriceFooter'
import AccessoriesPropertyPage from '../ui/components/property-pages/AccessoriesPropertyPage'
import AppearancePropertyPage from '../ui/components/property-pages/AppearancePropertyPage'
import BasePropertyPage from '../ui/components/property-pages/BasePropertyPage'
import TopPropertyPage from '../ui/components/property-pages/TopPropertyPage'
import WoodPropertyPage from '../ui/components/property-pages/WoodPropertyPage'

/** Navigation states where the main panel is summary / finalize, not a property step. The sidebar
 *  tab strip still renders and MUI Tabs require `value` to match a tab label — map these to
 *  Accessories so the active tab is always valid (SDK otherwise uses "none" and MUI warns). */
const SIDEBAR_TAB_FINALIZATION_STATES = [
  ApplicationTypes.NavigationState.Summary,
  ApplicationTypes.NavigationState.FinalizeQuote,
  ApplicationTypes.NavigationState.FinalizeCart,
] as const

const OWNER_OR_FINALIZE_MODES = ['owner', 'finalize'] as const

const READ_ONLY_MODE = ['read'] as const

const TableTabs = {
  tabs: [
    {
      label: 'Shape',
      navigationStates: [ApplicationTypes.NavigationState.Shape],
    },
    {
      label: 'Base',
      navigationStates: [ApplicationTypes.NavigationState.Base],
    },
    {
      label: 'Wood',
      navigationStates: [ApplicationTypes.NavigationState.Wood],
    },
    {
      label: 'Appearance',
      navigationStates: [ApplicationTypes.NavigationState.Appearance],
    },
    {
      label: 'Accessories',
      navigationStates: [ApplicationTypes.NavigationState.Accessories, ...SIDEBAR_TAB_FINALIZATION_STATES],
    },
  ],
}

// Review: These should be defined in yaml file
export const tableComponents = [
  // Use the regular default camera for preset thumbnails. Keeping this optic
  // in TypeScript avoids false-positive YAML schema diagnostics for this SDK
  // component while still using the declarative thumbnail rendering path.
  attachComponent
    .withComponent(saveTemplateThumbnailComponent, 'default')
    .withEntity('Table')
    .attach(
      memoEval(
        () => [],
        () => ({
          exportState: ApplicationTypes.ExportState.Thumbnail,
          width: 1280,
          height: 1280,
          fileFormat: 'PNG',
          backgroundColor: '#eeeeee',
        })
      )
    ),

  // Sidebar Tabs
  attachComponent
    .withComponent(sidebarTabsComponent, 'sidebar-tabs')
    .withEntity('Table')
    .withGradualFilter({ modes: OWNER_OR_FINALIZE_MODES })
    .attach(() => TableTabs),

  // Top
  attachComponent
    .withComponent(propertyPageComponent, 'table-top')
    .withEntity('Table')
    .withGradualFilter({
      navigationStates: [ApplicationTypes.NavigationState.Shape],
      modes: OWNER_OR_FINALIZE_MODES,
    })
    .attach(
      memoEval(
        s => [s.id, s.$top],
        (id, $top) => ({
          page: <TopPropertyPage tableId={id} top={$top} />,
        })
      )
    ),

  // Wood
  attachComponent
    .withComponent(propertyPageComponent, 'table-wood')
    .withEntity('Table')
    .withGradualFilter({
      navigationStates: [ApplicationTypes.NavigationState.Wood],
      modes: OWNER_OR_FINALIZE_MODES,
    })
    .attach(
      memoEval(
        s => [s.id, s.$top, s.$base],
        (id, $top, $base) => ({
          page: <WoodPropertyPage tableId={id} top={$top} base={$base} />,
        })
      )
    ),

  // Base
  attachComponent
    .withComponent(propertyPageComponent, 'table-base')
    .withEntity('Table')
    .withGradualFilter({
      navigationStates: [ApplicationTypes.NavigationState.Base],
      modes: OWNER_OR_FINALIZE_MODES,
    })
    .attach(
      memoEval(
        s => [s.id, s.$base],
        (id, $base) => ({
          page: <BasePropertyPage entityId={id} base={$base} />,
        })
      )
    ),

  // Appearance
  attachComponent
    .withComponent(propertyPageComponent, 'table-appearance')
    .withEntity('Table')
    .withGradualFilter({
      navigationStates: [ApplicationTypes.NavigationState.Appearance],
      modes: OWNER_OR_FINALIZE_MODES,
    })
    .attach(
      memoEval(
        s => [s.id, s.$top],
        (id, $top) => ({
          page: <AppearancePropertyPage tableId={id} top={$top} />,
        })
      )
    ),

  // Accessories
  attachComponent
    .withComponent(propertyPageComponent, 'table-accessories')
    .withEntity('Table')
    .withGradualFilter({
      navigationStates: [ApplicationTypes.NavigationState.Accessories],
      modes: OWNER_OR_FINALIZE_MODES,
    })
    .attach(
      memoEval(
        s => [s],
        table => ({
          page: <AccessoriesPropertyPage table={table} />,
        })
      )
    ),

  // Price Footer
  attachComponent
    .withEntity('Table')
    .withComponent(customPriceFooterComponent, 'price-footer')
    .attach(
      memoEval(
        s => [s.$price],
        price => ({
          node: <PriceFooter price={price} />,
        })
      )
    ),

  // Summary (owner / finalize: tab strip + “view summary” navigation)
  attachComponent
    .withComponent(customSummaryPageComponent, 'summary-page')
    .withEntity('Table')
    .withGradualFilter({ modes: OWNER_OR_FINALIZE_MODES })
    .attach(
      memoEval(
        () => [],
        () => ({
          summaryNavigationState: ApplicationTypes.NavigationState.Summary,
          page: <SummaryPage />,
        })
      )
    ),

  // Summary in read mode (no tabs; navigation may stay on a design step)
  attachComponent
    .withComponent(propertyPageComponent, 'read-summary')
    .withEntity('Table')
    .withGradualFilter({ modes: READ_ONLY_MODE })
    .attach(
      memoEval(
        () => [],
        () => ({
          page: <SummaryPage />,
        })
      )
    ),

  // Custom Summary Footer
  attachComponent
    .withEntity('Table')
    .withComponent(finalizeButtonComponent, 'custom-finalize-button')
    .attach(
      memoEval(
        s => [s],
        table => ({
          button: <CustomSummaryFooter table={table} />,
        })
      )
    ),

  // Finalize Dialog for Quote
  attachComponent
    .withEntity('Table')
    .withComponent(finalizePageComponent, 'finalize-quote')
    .attach(
      memoEval(
        () => [],
        () => ({
          page: <FinalizeQuoteDialog />,
          finalizeNavigationStates: [ApplicationTypes.NavigationState.FinalizeQuote],
        })
      )
    ),

  // Finalize Dialog for Quote
  attachComponent
    .withEntity('Table')
    .withComponent(propertyPageComponent, 'finalize-cart')
    .withGradualFilter({
      navigationStates: [ApplicationTypes.NavigationState.FinalizeCart],
      modes: OWNER_OR_FINALIZE_MODES,
    })
    .attach(
      memoEval(
        () => [],
        () => ({
          page: (
            <Dialog.Root variant="static" sx={{ width: '70%', maxWidth: '500px' }}>
              <FinalizeCartDialog />
            </Dialog.Root>
          ),
        })
      )
    ),
]
